import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import type { Proposal, DocumentSection } from '@shared/types';

/**
 * Forge's internal debug panel, mirroring `studio/StudioDebugPanel.tsx`:
 * concept, model, usage, validation issues, content readiness. Read-only,
 * collapsible, never styled for a customer (reuses the same
 * `.tpl-studio-debug*` dev-chrome classes — deliberately NOT `.tpl-*` mv2
 * tokens — rather than duplicating the CSS for a second debug panel).
 * Rendered only when the caller has already gated on `readOnly === false`
 * and `?debug=forge` (see `ForgePage`) — this component itself does no
 * gating so it stays trivially testable.
 */
export function ForgeDebugPanel({ section }: { proposal: Proposal; section: DocumentSection }): ReactElement | null {
  const [open, setOpen] = useState(true);
  const forge = section.forge;
  if (!forge) return null;

  return (
    <div className="tpl-studio-debug" data-open={open}>
      <button type="button" className="tpl-studio-debug-toggle" onClick={() => setOpen((v) => !v)}>
        Forge debug {open ? '▾' : '▸'} — {section.id}
      </button>
      {open ? (
        <div className="tpl-studio-debug-body">
          <Section title="Model">
            <Kv label="concept" value={forge.concept} />
            <Kv label="model" value={forge.model} />
            <Kv label="durationMs" value={forge.durationMs} />
            <Kv label="usage" value={forge.usage ? JSON.stringify(forge.usage) : undefined} />
          </Section>

          {forge.brief ? (
            <Section title="Slide brief">
              <Kv label="slideType" value={forge.brief.slideType} />
              <Kv label="treatment" value={forge.brief.treatment} />
              <Kv label="importance" value={forge.brief.importance} />
              <Kv label="density" value={forge.brief.density} />
            </Section>
          ) : null}

          {forge.validation ? (
            <Section title={`Validation (${forge.validation.passed ? 'passed' : 'failed'})`}>
              {forge.validation.issues.length ? (
                <Json value={forge.validation.issues} />
              ) : (
                <div className="tpl-studio-debug-empty">No issues</div>
              )}
            </Section>
          ) : null}

          {forge.readiness ? (
            <Section title="Content readiness">
              <Kv label="readiness" value={forge.readiness} />
              {forge.hydrated ? (
                <>
                  <Kv label="headline" value={forge.hydrated.headline} />
                  <Kv label="unresolvedRefs" value={forge.hydrated.unresolvedRefs.join(', ')} />
                  <Kv label="sourceRefs" value={forge.hydrated.sourceRefs.join(', ')} />
                </>
              ) : null}
            </Section>
          ) : null}

          {forge.contentDiagnostics?.length ? (
            <Section title={`Content diagnostics (${forge.contentDiagnostics.length})`}>
              <Json value={forge.contentDiagnostics} />
            </Section>
          ) : null}

          {forge.fallback ? (
            <Section title="Fallback">
              <Kv label="to" value={forge.fallback.to} />
              <Kv label="reason" value={forge.fallback.reason} />
            </Section>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }): ReactElement {
  return (
    <div className="tpl-studio-debug-section">
      <div className="tpl-studio-debug-section-title">{title}</div>
      {children}
    </div>
  );
}

function Kv({ label, value }: { label: string; value: unknown }): ReactElement {
  return (
    <div className="tpl-studio-debug-kv">
      <span className="tpl-studio-debug-key">{label}</span>
      <span className="tpl-studio-debug-value">{value === undefined || value === null || value === '' ? '(none)' : String(value)}</span>
    </div>
  );
}

function Json({ value }: { value: unknown }): ReactElement {
  return <pre className="tpl-studio-debug-json">{JSON.stringify(value, null, 2)}</pre>;
}

/** `?debug=forge` gate, read once per call — cheap enough to call per render. */
export function isForgeDebugRequested(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('debug') === 'forge';
}
